// apps/flux-web/src/components/retro/ProjectsTerminal.js
// Creative terminal-style projects display for Mission Control dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import {
    SolarPower,
    ElectricCar,
    BatteryChargingFull,
    AcUnit,
    Science,
    ChevronRight,
    FolderOpen
} from '@mui/icons-material';
import { useUnifiedProjects, vortxTypography } from '@vortx/shared-ui';

// App configuration with icons and colors
const APP_CONFIG = {
    site_dna: {
        label: 'SD',
        fullName: 'Site DNA',
        icon: SolarPower,
        color: '#ffa500',
        route: '/projects'
    },
    flux_ev: {
        label: 'EV',
        fullName: 'Flux EV',
        icon: ElectricCar,
        color: '#4CAF50',
        route: '/ev'
    },
    flux_ems: {
        label: 'EMS',
        fullName: 'Flux EMS',
        icon: BatteryChargingFull,
        color: '#b28c50',
        route: '/ems'
    },
    flux_cold: {
        label: 'COLD',
        fullName: 'Flux COLD',
        icon: AcUnit,
        color: '#2196F3',
        route: '/cold-storage'
    },
    flux_phd: {
        label: 'PhD',
        fullName: 'Flux PhD',
        icon: Science,
        color: '#9b59b6',
        route: '/phd'
    }
};

// Single project entry in terminal list
const ProjectEntry = ({ project, index, delay = 0 }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), delay);
        return () => clearTimeout(timer);
    }, [delay]);

    const enabledApps = Object.entries(project.apps || {})
        .filter(([_, config]) => config.enabled)
        .map(([app]) => app);

    const handleClick = () => {
        // If only one app enabled, open directly
        if (enabledApps.length === 1) {
            const app = enabledApps[0];
            const config = APP_CONFIG[app];
            if (config) {
                navigate(`${config.route}/${project.id}`);
            }
        } else {
            // Toggle expanded state to show options
            setExpanded(!expanded);
        }
    };

    const handleAppClick = (app, e) => {
        e.stopPropagation();
        const config = APP_CONFIG[app];
        if (config) {
            navigate(`${config.route}/${project.id}`);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <Box
            sx={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(5px)',
                transition: 'all 0.3s ease',
            }}
        >
            {/* Main project line */}
            <Box
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    py: 1.2,
                    px: 2,
                    cursor: 'pointer',
                    position: 'relative',
                    background: isHovered
                        ? 'linear-gradient(90deg, rgba(178, 140, 80, 0.15) 0%, transparent 100%)'
                        : expanded
                        ? 'rgba(178, 140, 80, 0.08)'
                        : 'transparent',
                    borderLeft: `2px solid ${isHovered ? '#b28c50' : expanded ? 'rgba(178, 140, 80, 0.5)' : 'transparent'}`,
                    transition: 'all 0.2s ease',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: isHovered ? '100%' : '0%',
                        background: 'linear-gradient(90deg, rgba(178, 140, 80, 0.05) 0%, transparent 100%)',
                        transition: 'width 0.3s ease',
                        pointerEvents: 'none',
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Expansion indicator */}
                    <ChevronRight
                        sx={{
                            fontSize: 14,
                            color: '#b28c50',
                            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                            opacity: enabledApps.length > 1 ? 1 : 0.3
                        }}
                    />

                    {/* Folder icon */}
                    <FolderOpen
                        sx={{
                            fontSize: 16,
                            color: isHovered ? '#b28c50' : '#888',
                            transition: 'color 0.2s ease'
                        }}
                    />

                    {/* Project name */}
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: isHovered ? '#f4f1ec' : '#c8c8c8',
                            flex: 1,
                            transition: 'color 0.2s ease',
                            textShadow: isHovered ? '0 0 10px rgba(178, 140, 80, 0.3)' : 'none',
                        }}
                    >
                        {project.name}
                    </Typography>

                    {/* App badges */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {enabledApps.map(app => {
                            const config = APP_CONFIG[app];
                            return (
                                <Tooltip key={app} title={config?.fullName || app} arrow>
                                    <Box
                                        sx={{
                                            px: 0.75,
                                            py: 0.25,
                                            fontSize: '0.65rem',
                                            fontFamily: vortxTypography.mono,
                                            fontWeight: 700,
                                            color: config?.color || '#888',
                                            border: `1px solid ${config?.color || '#888'}40`,
                                            background: `${config?.color || '#888'}15`,
                                            letterSpacing: '0.05em',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: config?.color || '#888',
                                                background: `${config?.color || '#888'}25`,
                                                boxShadow: `0 0 8px ${config?.color || '#888'}40`,
                                            }
                                        }}
                                    >
                                        [{config?.label || app}]
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {/* Last updated */}
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: '#666',
                            minWidth: 70,
                            textAlign: 'right',
                        }}
                    >
                        {formatDate(project.updated_at)}
                    </Typography>
                </Box>
            </Box>

            {/* Expanded app options */}
            {expanded && enabledApps.length > 1 && (
                <Box
                    sx={{
                        pl: 6,
                        pr: 2,
                        pb: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    {enabledApps.map(app => {
                        const config = APP_CONFIG[app];
                        const IconComponent = config?.icon;
                        return (
                            <Box
                                key={app}
                                onClick={(e) => handleAppClick(app, e)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 0.75,
                                    px: 1.5,
                                    cursor: 'pointer',
                                    background: 'rgba(0, 0, 0, 0.2)',
                                    border: `1px solid ${config?.color || '#888'}30`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        background: `${config?.color || '#888'}20`,
                                        borderColor: config?.color || '#888',
                                        boxShadow: `0 0 10px ${config?.color || '#888'}30`,
                                    }
                                }}
                            >
                                {IconComponent && (
                                    <IconComponent
                                        sx={{
                                            fontSize: 16,
                                            color: config?.color || '#888'
                                        }}
                                    />
                                )}
                                <Typography
                                    sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.75rem',
                                        color: '#c8c8c8',
                                    }}
                                >
                                    Open in {config?.fullName || app}
                                </Typography>
                                <ChevronRight
                                    sx={{
                                        fontSize: 14,
                                        color: config?.color || '#888',
                                        ml: 'auto'
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

// Main terminal component
const ProjectsTerminal = () => {
    const navigate = useNavigate();
    const { projects, loading, error } = useUnifiedProjects(null);

    // Debug logging
    useEffect(() => {
        console.log('[ProjectsTerminal] Debug:', {
            loading,
            error,
            projectsCount: projects?.length,
            projects: projects?.slice(0, 2) // Show first 2 projects
        });
    }, [loading, error, projects]);

    // Show max 5 most recently updated projects
    const recentProjects = [...projects]
        .sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at);
            const dateB = new Date(b.updated_at || b.created_at);
            return dateB - dateA;
        })
        .slice(0, 5);

    const handleViewAll = () => {
        navigate('/all-projects');
    };

    return (
        <Box sx={{ flexShrink: 0 }}>
            {/* Terminal Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 1.5,
                }}
            >
                <Typography
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        color: '#b28c50',
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        textShadow: '0 0 10px rgba(178, 140, 80, 0.3)',
                    }}
                >
                    &gt;&gt; ACTIVE MISSIONS
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(90deg, rgba(178, 140, 80, 0.5), transparent)',
                    }}
                />
                {!loading && projects.length > 0 && (
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.55rem',
                            color: '#666',
                        }}
                    >
                        {recentProjects.length}/{projects.length}
                    </Typography>
                )}
            </Box>

            {/* Terminal Window */}
            <Box
                sx={{
                    position: 'relative',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(178, 140, 80, 0.3)',
                    overflow: 'hidden',
                    minHeight: 160,
                    maxHeight: 280,
                    // Corner brackets
                    '&::before, &::after': {
                        content: '""',
                        position: 'absolute',
                        width: '10px',
                        height: '10px',
                        borderColor: 'rgba(178, 140, 80, 0.6)',
                        zIndex: 10,
                    },
                    '&::before': {
                        top: 4,
                        left: 4,
                        borderTop: '2px solid',
                        borderLeft: '2px solid',
                    },
                    '&::after': {
                        bottom: 4,
                        right: 4,
                        borderBottom: '2px solid',
                        borderRight: '2px solid',
                    }
                }}
            >
                {/* Scanline overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.15) 0px, transparent 2px, transparent 4px)',
                        pointerEvents: 'none',
                        zIndex: 5,
                    }}
                />

                {/* Content */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        maxHeight: 280,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(0, 0, 0, 0.3)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(178, 140, 80, 0.3)',
                            '&:hover': {
                                background: 'rgba(178, 140, 80, 0.5)',
                            }
                        }
                    }}
                >
                    {error ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 160,
                                px: 3,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: '#ff6b6b',
                                    textAlign: 'center',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                &gt; ERROR: {error}
                            </Typography>
                        </Box>
                    ) : loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 160,
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: '#b28c50',
                                    filter: 'drop-shadow(0 0 8px rgba(178, 140, 80, 0.5))'
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                    color: '#888',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                SCANNING MISSION DATABASE...
                            </Typography>
                        </Box>
                    ) : recentProjects.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 160,
                                px: 3,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: '#666',
                                    textAlign: 'center',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                &gt; NO ACTIVE MISSIONS
                                <br />
                                <Box component="span" sx={{ color: '#555', fontSize: '0.7rem' }}>
                                    Initialize first mission to begin
                                </Box>
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ py: 1 }}>
                            {recentProjects.map((project, index) => (
                                <ProjectEntry
                                    key={project.id}
                                    project={project}
                                    index={index}
                                    delay={index * 50}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Phosphor glow effect */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(ellipse at center, rgba(178, 140, 80, 0.03) 0%, transparent 70%)',
                        pointerEvents: 'none',
                        zIndex: 2,
                    }}
                />
            </Box>

            {/* Terminal Footer */}
            {!loading && projects.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1,
                        px: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.6rem',
                            color: '#666',
                            letterSpacing: '0.05em',
                        }}
                    >
                        {recentProjects.length < projects.length
                            ? `Showing ${recentProjects.length} most recent`
                            : `${projects.length} total mission${projects.length !== 1 ? 's' : ''}`}
                    </Typography>

                    {projects.length > 5 && (
                        <Box
                            onClick={handleViewAll}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    '& .arrow': {
                                        transform: 'translateX(3px)',
                                    },
                                    '& .text': {
                                        color: '#b28c50',
                                        textShadow: '0 0 8px rgba(178, 140, 80, 0.4)',
                                    }
                                }
                            }}
                        >
                            <Typography
                                className="text"
                                sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.65rem',
                                    color: '#888',
                                    letterSpacing: '0.05em',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                VIEW ALL
                            </Typography>
                            <ChevronRight
                                className="arrow"
                                sx={{
                                    fontSize: 12,
                                    color: '#b28c50',
                                    transition: 'transform 0.2s ease',
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ProjectsTerminal;
